import React, { useMemo, useEffect, useState, useContext, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { store } from "../components/Store";
import MicroModal from "micromodal";
import { Icon } from '@iconify/react';
import { useBookData } from "./useBookData";
import useIsMobile from "../js/utilities/useIsMobile";
import useObtenerSubsecciones from "../js/translations/useObtenerSubsecciones";


//componentes

import ModalAlertas from "../components/ModalAlertas";
import { ModalCompartir } from "../components/modals/modal-compartir";
import InfoBasica from "./informacionFicha/InfoBasica";
import InfoDetalle from "./informacionFicha/InfoDetalle";
import SkeletonFichaLibro from "../skeletons/SkeletonsFichaLibro/SkeletonFichaLibro";

import { InvitacionLectura } from "./invitacion-lectura.js";
import { NotAvailableBook } from "./not-available-book";

import { SolicitudDemo } from "../components/solicitud-demo/index.js";

import { Helmet } from "react-helmet";
import { useBookSelection } from "./hooks/use-book-selection.js";
import { useContactRequest } from "../components/solicitud-demo/hook/useContactRequest.js";

import { CoverSection } from "./components/coverSection.js";
import { Activity } from "./components/activity.js";
import { useUserBooks } from "../hooks/useUserBooks.js";
import { useLanguage } from "../hooks/use-language.js";
import { useId } from "../hooks/use-id.js";
import { Others } from "./components/others.js";
import { buildMakeMakeComercialUrlByEnv } from "../util/host-utilities.js";

/**
 * Componente que no recibe props. Este renderiza la pantalla de ficha del libro con su información correspondiente. Entre sus funciones principales estan obtener la información del libro, definir si el libro puede ser descargado y descargar libro. Este hace uso de los componentes Cover, ModalAlertas, ModalCompartir, InfoBasica, InfoDetalle, InvitacionLectura y AlertaLimite. 
 * @returns Ficha
 */
const Ficha = () => {
  const params = useParams();
  const { isReady, textosSubSecciones: textoFicha } = useObtenerSubsecciones('ficha_libro');

  const globalState = useContext(store);
  const { state } = globalState;
  const isLogged = state.sesionActiva !== null && state.sesionActiva === "SI";
  const lang = useLanguage();
  const isMobile = useIsMobile();
  
  
  //Datos ficha
  let book = useBookData(params?.idLibro);
  const isFree = book?.metadata?.gratis === 1 || book?.metadata?.gratis === true;
  const metadata = useMemo(() => book.metadata, [book.metadata])
  const bookAvailable = (metadata?.territorio && typeof metadata?.territorio === "string" && metadata?.territorio?.trim()?.length > 0);
  const userBooks = useUserBooks();
 
  const isBookFromUserBooks =userBooks?.books?.map(book=>book?.idLibro)?.includes(book?.id);

  const [urlLibro, setUrlLibro] = useState("");

  //Acciones ficha
  const [activeTab, setActiveTab] = useState("informacion-basica");

  const bookSelectionManager = useBookSelection({ bookId: params?.idLibro })
  const isBookSelectionMode = bookSelectionManager.isBookSelectionMode;

  const contactRequest = useContactRequest();

  const panelRef = useRef(null);
  const commercialBooksUrl = buildMakeMakeComercialUrlByEnv({ path: "/libros" });

  const titleId = useId('titulo-ficha');
  const tabInfoBasicaId = useId('tab-info-basica');
  const tabMasDetallesId = useId('tab-mas-detalles');
  const panelInfoBasicaId = useId('panel-info-basica');
  const panelMasDetallesId = useId('panel-mas-detalles');

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.focus();
    }
  }, [activeTab]);

  useEffect(() => {
    if (isLogged) {
      contactRequest.setVisible(false)
    } else {
      contactRequest.setVisible(true)
    }
  }, []);

  //Url libro: actualiza el estado urlLibro
  useEffect(() => {
    let href;
    if (isLogged && state.cantidadLibros === 0) {
      href = "/mi-panel/mi-coleccion";
    }
    else if (isLogged && state.cantidadLibros > 0) {
      href = `/visor/${params?.idLibro}`;
    }
    else if (!isLogged && book?.metadata?.gratis === 1) {
      href = `/visor/${params?.idLibro}`;
    }
    else {
      href = `/login?bookT=/visor/${params?.idLibro}/`;
    }
    setUrlLibro(href);
  }, [book, isLogged, params.idLibro, state.cantidadLibros])



  /**
     * Esta función retorna el link del blog en caso de que el libro este relacionado. 
   */
  const linkBlog = () => {
    if (metadata?.linkBlog !== undefined && metadata?.linkBlog !== '') {
      return (
        <Link
          to={metadata.linkBlog}
          className="ficha-link-blog focusable-secondary d-inline-block mb-4"
        >
          {textoFicha?.mas_detalles?.entrada || 'Entrada relacionada del blog'}{" "}
          <i className="fas fa-external-link-alt"></i>
        </Link>
      )
    }
  }

  const handleTabKeyDown = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const tabs = isLogged
        ? ["informacion-basica", "mas-detalles"]
        : ["informacion-basica"];

      const currentIndex = tabs.indexOf(activeTab);
      let nextIndex;

      if (e.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else {
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      }

      setActiveTab(tabs[nextIndex]);
    }
  };

  const [showActivity, setShowActivity] = useState(window.innerWidth < 1200 ? true : false);
      
  useEffect(() => {
      const setM = () => {
          let validacion;
          if (window.innerWidth < 1200) {
              validacion = true;
          } else {
              validacion = false;
          }
          setShowActivity(validacion)
      }
      setM();
      window.addEventListener('resize', setM)
      return () => {
          window.removeEventListener('resize', setM)
      }
  }, [])

  //render FICHA
  if (book?.loading?.bookData) {
    return (
      <SkeletonFichaLibro
        isLogged={isLogged}
        recomendados={book.recommendedBooks}
      />
    )
  } else {
    return (
      <>

        <Helmet>
          <title>{`MakeMake - ${metadata.titulo}`}</title>
          <meta name="description" content={lang === 'es' ? metadata?.descripcion || 'N/A' : metadata?.descripcionEn || metadata?.descripcion || 'N/A'} />
          <link rel="canonical" href={`https://makemake.com.co/fichas/${params?.idLibro}/${params?.nombreLibro}`} />
        </Helmet>


        {isReady &&
          <>
            <div className="main-container">
              <InvitacionLectura />
              <section className={`container contenedor-ficha px-0 pt-4`} aria-labelledby={titleId}>
              <div className={`${isLogged ? 'px-4 px-lg-5 pt-3' : 'px-4  pt-4 px-lg-5 pt-lg-5'}`}>
                {
                  isLogged && !bookSelectionManager.loading && isBookSelectionMode && bookAvailable && !isFree ?
                    <div className="book-selection-bar w-100 p-2 my-2 d-block px-3">
                      <div className="w-100 d-flex justify-content-between align-items-center">
                        <div>
                          <p className="font-weight-bold onbackground-primary--color">{textoFicha?.seleccion_libros?.libros_agregados || 'Libros añadidos'} <span className="secondary-inverse--color">{`(${bookSelectionManager.currentSelectedBooksLength}/${bookSelectionManager.maxBooks})`}</span></p>
                        </div>
                        <div className="d-flex align-items-center">
                          {
                            bookSelectionManager.isCurrentAdded ?
                              <>
                                <p className="font-weight-bold onbackground-primary--color mr-2 d-flex justify-content-center align-items-center"><Icon icon="ic:outline-check" width={24} />{textoFicha?.seleccion_libros?.agregado || 'Añadido'}</p>
                                <button className="remove-button focusable-primary" onClick={() => bookSelectionManager.remove()}>{textoFicha?.seleccion_libros?.remover || 'Remover'}</button>
                              </>
                              :
                              <button className="add-button focusable-primary mr-2" onClick={() => bookSelectionManager.add()} disabled={bookSelectionManager.currentSelectedBooksLength > bookSelectionManager.maxBooks - 1}>{textoFicha?.seleccion_libros?.agregar || 'Agregar'}</button>
                          }

                        </div>
                      </div>
                    </div>
                    :
                    null
                }
                {
                  !isLogged && !bookAvailable &&
                  <NotAvailableBook text={textoFicha?.disponibilidad_libro?.no_disponible || 'Lo sentimos, Este libro actualmente no se encuentra disponible en nuestro catálogo.'} />
                }
                {
                  isLogged && userBooks.loadCompleted && (!isBookFromUserBooks) &&
                  <NotAvailableBook  text={textoFicha?.disponibilidad_libro_en_paquete?.no_disponible || 'Lo sentimos, actualmente este libro no se encuentra en tu colección.'} />
                }
                <div className="indicator">
                  <Icon icon="tabler:book" width={20} />
                  <p className="body-base">{textoFicha?.interfaz?.libro || 'Libro'}</p>
                </div>

                <div className="title-ficha">
                  <div>
                    <h1 className="heading-3 title" id={titleId}>{metadata.titulo}</h1>
                  </div>
                  <div className="content-nav-ficha">
                    <div className="sr-only" aria-live="polite">
                      {activeTab === "informacion-basica" &&  textoFicha?.accesibilidad?.basic_info ||  "Mostrando información básica"}
                      {activeTab === "mas-detalles" && textoFicha?.accesibilidad?.more_info || "Mostrando más detalles"}
                    </div>

                    <nav className="nav-ficha d-flex" role="tablist" aria-label={textoFicha?.accesibilidad?.book_info || "Información del libro"}>
                      <button
                        id={tabInfoBasicaId}
                        role="tab"
                        aria-selected={activeTab === "informacion-basica"}
                        aria-controls={ panelInfoBasicaId }
                        tabIndex={activeTab === "informacion-basica" ? 0 : -1}
                        onClick={() => setActiveTab("informacion-basica")}
                        onKeyDown={handleTabKeyDown}
                        className={`px-2 px-lg-3 py-2 nav-button-ficha focusable-red ${activeTab === "informacion-basica" && 'nav-button-ficha-active'}`}
                      >
                        {textoFicha?.info_basica?.titulo_pestana || 'Información básica'}
                      </button>
                      {isLogged &&
                        <button
                          id={tabMasDetallesId}
                          role="tab"
                          aria-selected={activeTab === "mas-detalles"}
                          aria-controls={panelMasDetallesId}
                          tabIndex={activeTab === "mas-detalles" ? 0 : -1}
                          onClick={() => setActiveTab("mas-detalles")}
                          onKeyDown={handleTabKeyDown}
                          className={`px-2 px-lg-3 py-2 ml-3 nav-button-ficha focusable-red ${activeTab === "mas-detalles" && 'nav-button-ficha-active'}`}
                        >
                          {textoFicha?.mas_detalles?.titulo_pestana || 'Más detalles'}
                        </button>
                      }
                    </nav>
                  </div>
                </div>
                <div className={`d-flex py-lg-3 mt-4`}>
                  {!isMobile &&
                    <CoverSection
                      metadata={metadata}
                      textoFicha={textoFicha}
                    />
                  }
                  <section className="w-100">
                    <div
                      ref={panelRef}
                      tabIndex="-1"
                      role="tabpanel"
                      id={activeTab === "informacion-basica" ?  panelInfoBasicaId  : panelMasDetallesId}
                      aria-labelledby={activeTab === "informacion-basica" ? tabInfoBasicaId : tabMasDetallesId}
                      className="pl-lg-5 pr-lg-3 content-info-ficha"
                    >
                      {
                        !isMobile &&
                        <Activity metadata={metadata} textoFicha={textoFicha} />
                      }

                      {activeTab === "informacion-basica" ?
                        <InfoBasica
                          textoFicha={textoFicha}
                          metadata={metadata}
                          idioma={lang}
                        />
                        :
                        <InfoDetalle textoFicha={textoFicha} metadata={metadata} idioma={lang} />
                      }

                      <div>
                        {/* blog */}
                        {linkBlog()}

                        {/* leer */}
                        <div className={`row mx-0 justify-content-md-end`}>

                          {
                            ((isLogged && isBookFromUserBooks) || (!isLogged && bookAvailable)) &&
                            <Link
                              to={urlLibro}
                              className="focusable-red buttonv2 button-primary mt-4 focusable-red px-5 d-block ficha-btn-enviar "
                              style={{ maxWidth: 285 }}
                              aria-disabled={metadata.limiteDeLecturasAlcanzado}
                              onClick={(e) => {
                                if (metadata.limiteDeLecturasAlcanzado) {
                                  e.preventDefault();
                                  MicroModal.show("modal-alerts", {
                                    awaitCloseAnimation: true,
                                  });
                                }
                              }}
                            >
                              {textoFicha?.botones?.leer || 'Leer'}
                              <Icon icon="tabler:chevron-right" width={20} className="ml-2" />
                            </Link>
                          }
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
              {!contactRequest?.state?.visible &&
                <div className={`ficha-otros-temas p-5`}>
                  <Others book={book} />
                </div>
              }
              
              {contactRequest?.state?.visible && <div className="gradient-demo"></div>}
              </section>

              {contactRequest?.state?.visible &&
                <SolicitudDemo
                  className={'ficha-demo'}
                  keepReadingHref={commercialBooksUrl}
                  txtButton={textoFicha?.botones?.catalogo || 'ver más libros'}
                />
              }


            </div>


            <ModalAlertas
              id="modal-alerts"
              message={textoFicha?.mas_detalles?.modal_alerta?.mensaje || "Ya no tienes libros disponibles"}
              description={
                textoFicha?.mas_detalles?.modal_alerta?.descripcion || "Has superado el límite de libros que puedes leer en un día. Para ampliar la capacidad de tu suscripción, escríbenos a contacto@makemake.com.co`"
              }
            />
            <ModalCompartir idModal={'modal-compartir-libro'} libro={metadata} />
          </>
        }
      </>
    )
  }

}

export default Ficha;
